import { useEffect, useRef, useState } from 'react';
import HBoxFax, { type FaxManager, type HBoxFaxConfig } from 'hbox-fax';
import { API_CONFIG } from '@/config/envConfig';
import { useAuth } from '@/contexts/AuthContext';
import { clinicsGraphqlService } from '@/services/api/clinics/clinicsGraphqlService';

const FaxPage = () => {
  const { user, token, hasuraToken } = useAuth();
  const containerRef = useRef<HTMLDivElement>(null);
  const managerRef = useRef<FaxManager | null>(null);
  const [faxConfig, setFaxConfig] = useState<HBoxFaxConfig | null>(null);

  useEffect(() => {
    document.title = 'Fax | Admin';
  }, []);

  useEffect(() => {
    if (!token || !hasuraToken) {
      setFaxConfig(null);
      return;
    }

    let cancelled = false;

    const buildConfig = async () => {
      const config: HBoxFaxConfig = {
        theme: 'light',
        apiBaseUrl: API_CONFIG.API_BASE_FAX_SERVICE || '',
        apiToken: token,
        graphqlBaseUrl: API_CONFIG.GRAPHQL_ENDPOINT,
        graphqlToken: hasuraToken,
        ...((user?.type === 'EA' || user?.type === 'ED')
          ? { profile: { type: 'EA' as const } }
          : user?.type === 'ES'
            ? { profile: { type: 'ES' as const } }
            : user?.type === 'PA'
              ? { profile: { type: 'PA' } }
              : {}),
      };

      if (user?.type === 'PA' && user?.id) {
        const clinics = await clinicsGraphqlService.getClinicsForUser(Number(user.id), hasuraToken);
        config.clinics = clinics.map((clinic) => ({ id: clinic.id, state_id: null, name: clinic.name }));
      }

      if (!cancelled) {
        setFaxConfig(config);
      }
    };

    buildConfig();

    return () => {
      cancelled = true;
    };
  }, [hasuraToken, token, user?.type, user?.id]);

  useEffect(() => {
    const container = containerRef.current;

    if (!container || managerRef.current || !faxConfig) {
      return;
    }

    managerRef.current = HBoxFax.init(container, faxConfig);

    return () => {
      if (managerRef.current) {
        managerRef.current.destroy();
        managerRef.current = null;
      }
    };
  }, [faxConfig]);

  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto">
        <div
          id="fax-container"
          ref={containerRef}
          className="min-h-[70vh]"
        />
      </main>
    </div>
  );
};

export default FaxPage;
